import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosAdmin from "../../../../api/apiAdmin";
import { successToast, errorToast } from "../../toastAlerts/ToastAlerts";
import ContinueComponent from "../../toastAlerts/ContinueComponent";
import { BsTrash, BsPlusLg, BsBoxArrowUpRight } from "react-icons/bs";
import { PencilSquare } from "react-bootstrap-icons";

/**
 * Admin for the landing-page shop directory.
 *
 * Categories and shops share one screen: a category exists only to file shops
 * under, so making the admin navigate away to create one would put a detour in
 * the middle of adding a shop.
 */

const EMPTY = {
   id: null,
   name: "",
   url: "",
   country_id: "",
   shop_category_id: "",
   sort: 0,
   active: true,
   img: null,        // existing path
   imgFile: null,    // newly picked File
};

const field =
   "rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 " +
   "dark:text-gray-200 px-3 py-2 text-sm outline-none focus:border-teal-500";
const fieldFull = `${field} w-full`;
const label = "text-[12px] font-bold text-[#777a83] dark:text-gray-300";

const ShopsPage = () => {
   const { t } = useTranslation();

   const [shops, setShops] = useState([]);
   const [categories, setCategories] = useState([]);
   const [countries, setCountries] = useState([]);
   const [loading, setLoading] = useState(true);
   const [saving, setSaving] = useState(false);

   const [form, setForm] = useState(EMPTY);
   const [filterCountry, setFilterCountry] = useState("");
   const [filterCategory, setFilterCategory] = useState("");

   const [newCategory, setNewCategory] = useState({ name_ge: "", name_en: "", sort: 0 });
   const [confirmShop, setConfirmShop] = useState(false);
   const [confirmCategory, setConfirmCategory] = useState(false);
   const [pendingId, setPendingId] = useState(null);

   const load = async () => {
      try {
         const [s, c, co] = await Promise.all([
            axiosAdmin.get("/shops"),
            axiosAdmin.get("/shopCategories"),
            axiosAdmin.get("/countries"),
         ]);
         setShops(s.data || []);
         setCategories(c.data || []);
         setCountries(co.data || []);
      } catch (e) {
         errorToast(t("admin_something_wrong") || "შეცდომა");
      } finally {
         setLoading(false);
      }
   };

   useEffect(() => {
      load();
   }, []);

   const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

   const visible = useMemo(
      () =>
         shops.filter(
            (s) =>
               (!filterCountry || Number(s.country_id) === Number(filterCountry)) &&
               (!filterCategory ||
                  Number(s.shop_category_id) === Number(filterCategory))
         ),
      [shops, filterCountry, filterCategory]
   );

   /* --------------------------------------------------------------- shops */

   const saveShop = async () => {
      if (!form.name.trim() || !form.url.trim()) {
         errorToast(t("admin_fill_required") || "შეავსეთ სახელი და ბმული");
         return;
      }
      setSaving(true);
      try {
         // multipart even without a file: the endpoint accepts an upload and
         // PHP will not parse a multipart body on PUT, which is why both
         // create and update are POSTs.
         const fd = new FormData();
         fd.append("name", form.name);
         fd.append("url", form.url);
         if (form.country_id) fd.append("country_id", form.country_id);
         if (form.shop_category_id) fd.append("shop_category_id", form.shop_category_id);
         fd.append("sort", form.sort || 0);
         fd.append("active", form.active ? 1 : 0);
         if (form.imgFile) fd.append("img", form.imgFile);

         await axiosAdmin.post(form.id ? `/shops/${form.id}` : "/shops", fd, {
            headers: { "Content-Type": "multipart/form-data" },
         });
         successToast(t("admin_added_success") || "შენახულია");
         setForm(EMPTY);
         load();
      } catch (e) {
         errorToast(
            e?.response?.data?.error
               ? Object.values(e.response.data.error).flat().join(", ")
               : t("admin_something_wrong") || "შეცდომა"
         );
      } finally {
         setSaving(false);
      }
   };

   const deleteShop = async (id) => {
      try {
         await axiosAdmin.delete(`/shops/${id}`);
         successToast(t("admin_deleted_success") || "წაშლილია");
         if (form.id === id) setForm(EMPTY);
         load();
      } catch (e) {
         errorToast(t("admin_something_wrong") || "შეცდომა");
      }
   };

   /* ---------------------------------------------------------- categories */

   const addCategory = async () => {
      if (!newCategory.name_ge.trim()) return;
      try {
         await axiosAdmin.post("/shopCategories", newCategory);
         setNewCategory({ name_ge: "", name_en: "", sort: 0 });
         successToast(t("admin_added_success") || "დამატებულია");
         load();
      } catch (e) {
         errorToast(t("admin_something_wrong") || "შეცდომა");
      }
   };

   const deleteCategory = async (id) => {
      try {
         await axiosAdmin.delete(`/shopCategories/${id}`);
         successToast(t("admin_deleted_success") || "წაშლილია");
         load();
      } catch (e) {
         errorToast(t("admin_something_wrong") || "შეცდომა");
      }
   };

   const categoryName = (id) => {
      const c = categories.find((x) => Number(x.id) === Number(id));
      return c ? c.name_ge : "—";
   };
   const countryName = (id) => {
      const c = countries.find((x) => Number(x.id) === Number(id));
      return c ? c.name : "—";
   };

   return (
      // The admin shell's sidebar and header are fixed, so every page carries
      // its own offset — without it the page renders underneath them.
      <div
         className="xl:ml-[280px] mt-[55px] bg-slate-100 dark:bg-slate-900 min-h-screen
            p-10 sm:p-5 pb-24 flex flex-col gap-5"
      >
         <h1 className="text-xl font-bold text-[#777a83] dark:text-gray-200">
            {t("admin_shops") || "მაღაზიები"}
         </h1>

         <div className="flex gap-5 md:flex-col">
            {/* ------------------------------------------------ shop form */}
            <div className="w-4/12 md:w-full bg-white dark:bg-slate-700 rounded-xl p-5 flex flex-col gap-3 self-start">
               <p className="font-bold text-[#777a83] dark:text-gray-200">
                  {form.id
                     ? t("admin_edit") || "რედაქტირება"
                     : t("admin_add_new") || "ახლის დამატება"}
               </p>

               <div className="flex flex-col gap-1">
                  <span className={label}>{t("admin_name") || "დასახელება"} *</span>
                  <input
                     className={fieldFull}
                     value={form.name}
                     onChange={(e) => set("name", e.target.value)}
                  />
               </div>

               <div className="flex flex-col gap-1">
                  <span className={label}>{t("admin_link") || "ბმული"} *</span>
                  <input
                     className={fieldFull}
                     placeholder="https://..."
                     value={form.url}
                     onChange={(e) => set("url", e.target.value)}
                  />
               </div>

               <div className="flex flex-col gap-1">
                  <span className={label}>{t("admin_country") || "ქვეყანა"}</span>
                  <select
                     className={fieldFull}
                     value={form.country_id}
                     onChange={(e) => set("country_id", e.target.value)}
                  >
                     <option value="">—</option>
                     {countries.map((c) => (
                        <option key={c.id} value={c.id}>
                           {c.name}
                        </option>
                     ))}
                  </select>
               </div>

               <div className="flex flex-col gap-1">
                  <span className={label}>{t("admin_category") || "კატეგორია"}</span>
                  <select
                     className={fieldFull}
                     value={form.shop_category_id}
                     onChange={(e) => set("shop_category_id", e.target.value)}
                  >
                     <option value="">—</option>
                     {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                           {c.name_ge}
                        </option>
                     ))}
                  </select>
               </div>

               <div className="flex gap-3">
                  <div className="flex flex-col gap-1 w-1/2">
                     <span className={label}>{t("admin_sort") || "სორტირება"}</span>
                     <input
                        type="number"
                        className={fieldFull}
                        value={form.sort}
                        onChange={(e) => set("sort", e.target.value)}
                     />
                  </div>
                  <label className="flex items-end gap-2 w-1/2 pb-2 cursor-pointer">
                     <input
                        type="checkbox"
                        checked={!!form.active}
                        onChange={(e) => set("active", e.target.checked)}
                     />
                     <span className={label}>{t("admin_active") || "აქტიური"}</span>
                  </label>
               </div>

               <div className="flex flex-col gap-1">
                  <span className={label}>{t("admin_logo") || "ლოგო"}</span>
                  <input
                     type="file"
                     accept="image/*"
                     className="text-xs dark:text-gray-300"
                     onChange={(e) => set("imgFile", e.target.files?.[0] || null)}
                  />
                  {/* Editing without picking a new file keeps the old logo —
                      the endpoint only overwrites when a file is sent. */}
                  {form.img && !form.imgFile && (
                     <img
                        src={`${process.env.REACT_APP_API_URL}/storage/${form.img}`}
                        alt=""
                        className="mt-1 max-h-12 object-contain self-start"
                     />
                  )}
               </div>

               <div className="flex gap-2 mt-1">
                  <button
                     onClick={saveShop}
                     disabled={saving}
                     className="flex-1 rounded-lg bg-teal-500 disabled:opacity-50 text-white
                        font-bold text-sm py-2 flex items-center justify-center gap-2"
                  >
                     <BsPlusLg />
                     {t("admin_save") || "შენახვა"}
                  </button>
                  {form.id && (
                     <button
                        onClick={() => setForm(EMPTY)}
                        className="rounded-lg bg-slate-400 text-white font-bold text-sm py-2 px-4"
                     >
                        {t("admin_cancel") || "გაუქმება"}
                     </button>
                  )}
               </div>
            </div>

            {/* ------------------------------------------------- shop list */}
            <div className="w-8/12 md:w-full flex flex-col gap-3">
               <div className="bg-white dark:bg-slate-700 rounded-xl p-4 flex gap-3 flex-wrap">
                  <select
                     className={field}
                     value={filterCountry}
                     onChange={(e) => setFilterCountry(e.target.value)}
                  >
                     <option value="">{t("all_countries") || "ყველა ქვეყანა"}</option>
                     {countries.map((c) => (
                        <option key={c.id} value={c.id}>
                           {c.name}
                        </option>
                     ))}
                  </select>
                  <select
                     className={field}
                     value={filterCategory}
                     onChange={(e) => setFilterCategory(e.target.value)}
                  >
                     <option value="">{t("all_categories") || "ყველა კატეგორია"}</option>
                     {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                           {c.name_ge}
                        </option>
                     ))}
                  </select>
                  <span className="text-xs self-center text-[#989ba5]">
                     {visible.length} / {shops.length}
                  </span>
               </div>

               {loading ? (
                  <div className="animate-pulse bg-slate-100 dark:bg-slate-700 h-40 rounded-xl" />
               ) : (
                  <div className="grid grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-3">
                     {visible.map((s) => (
                        <div
                           key={s.id}
                           className={`bg-white dark:bg-slate-700 rounded-xl p-4 flex flex-col gap-2
                              ${s.active ? "" : "opacity-50"}`}
                        >
                           <div className="flex items-center gap-2">
                              {s.img ? (
                                 <img
                                    src={`${process.env.REACT_APP_API_URL}/storage/${s.img}`}
                                    alt=""
                                    className="w-10 h-10 object-contain shrink-0"
                                 />
                              ) : (
                                 <div className="w-10 h-10 shrink-0 rounded bg-slate-100 dark:bg-slate-600" />
                              )}
                              <div className="min-w-0">
                                 <p className="font-bold text-[14px] text-[#777a83] dark:text-gray-200 truncate">
                                    {s.name}
                                 </p>
                                 <a
                                    href={s.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[11px] text-teal-600 flex items-center gap-1 truncate"
                                 >
                                    <BsBoxArrowUpRight className="shrink-0" />
                                    <span className="truncate">{s.url}</span>
                                 </a>
                              </div>
                           </div>
                           <p className="text-[11px] text-[#989ba5]">
                              {countryName(s.country_id)} · {categoryName(s.shop_category_id)}
                           </p>
                           <div className="flex gap-2">
                              <button
                                 onClick={() =>
                                    setForm({
                                       id: s.id,
                                       name: s.name,
                                       url: s.url,
                                       country_id: s.country_id || "",
                                       shop_category_id: s.shop_category_id || "",
                                       sort: s.sort || 0,
                                       active: !!s.active,
                                       img: s.img,
                                       imgFile: null,
                                    })
                                 }
                                 className="flex-1 rounded-lg bg-violet-400 text-white text-xs font-bold
                                    py-2 flex items-center justify-center gap-1"
                              >
                                 <PencilSquare />
                                 {t("admin_edit") || "რედაქტირება"}
                              </button>
                              <button
                                 onClick={() => {
                                    setPendingId(s.id);
                                    setConfirmShop(true);
                                 }}
                                 className="rounded-lg bg-red-400 text-white px-3 py-2"
                              >
                                 <BsTrash />
                              </button>
                           </div>
                        </div>
                     ))}
                  </div>
               )}
            </div>
         </div>

         {/* ---------------------------------------------------- categories */}
         <div className="bg-white dark:bg-slate-700 rounded-xl p-5 flex flex-col gap-3">
            <p className="font-bold text-[#777a83] dark:text-gray-200">
               {t("admin_shop_categories") || "მაღაზიების კატეგორიები"}
            </p>
            <div className="flex gap-2 flex-wrap items-end">
               <input
                  className={field}
                  placeholder={t("admin_name_ge") || "დასახელება (ქარ)"}
                  value={newCategory.name_ge}
                  onChange={(e) =>
                     setNewCategory((p) => ({ ...p, name_ge: e.target.value }))
                  }
               />
               <input
                  className={field}
                  placeholder={t("admin_name_en") || "Name (EN)"}
                  value={newCategory.name_en}
                  onChange={(e) =>
                     setNewCategory((p) => ({ ...p, name_en: e.target.value }))
                  }
               />
               <input
                  type="number"
                  className={`${field} w-20`}
                  value={newCategory.sort}
                  onChange={(e) =>
                     setNewCategory((p) => ({ ...p, sort: e.target.value }))
                  }
               />
               <button
                  onClick={addCategory}
                  className="rounded-lg bg-teal-500 text-white font-bold text-sm px-4 py-2"
               >
                  {t("admin_add") || "დამატება"}
               </button>
            </div>
            <div className="flex gap-2 flex-wrap">
               {categories.map((c) => (
                  <span
                     key={c.id}
                     className="flex items-center gap-2 rounded-full bg-slate-100 dark:bg-slate-600
                        px-3 py-1 text-xs text-[#777a83] dark:text-gray-200"
                  >
                     {c.name_ge}
                     <em className="not-italic text-[#989ba5]">({c.shops_count ?? 0})</em>
                     <BsTrash
                        className="cursor-pointer text-red-400"
                        onClick={() => {
                           setPendingId(c.id);
                           setConfirmCategory(true);
                        }}
                     />
                  </span>
               ))}
            </div>
            {/* Deleting a category must not take its shops off the site with
                it — the server unfiles them instead. */}
            <p className="text-[11px] text-[#989ba5]">
               {t("admin_shop_category_delete_note") ||
                  "კატეგორიის წაშლისას მისი მაღაზიები რჩება, უბრალოდ კატეგორიის გარეშე."}
            </p>
         </div>

         <ContinueComponent
            showFlag={confirmShop}
            actionFunction={deleteShop}
            setShowContinue={setConfirmShop}
            id={pendingId}
         />
         <ContinueComponent
            showFlag={confirmCategory}
            actionFunction={deleteCategory}
            setShowContinue={setConfirmCategory}
            id={pendingId}
         />
      </div>
   );
};

export default ShopsPage;
